import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiCheck, FiSquare, FiChevronDown, FiChevronUp } from "react-icons/fi";
import useDropdownPosition from "../hooks/useDropdownPosition";
import "../style/SrMultiSelectDropdown.css";

const MENU_MAX_HEIGHT = 304;

const getOptionSerial = (option) => option.srNo || option.srno || "";
const getOptionLabel = (option) => {
  const optionSerial = getOptionSerial(option);
  const capacity = option.cylinderCapacity || "";
  const batchNo = option.batchNo || "";
  return `${optionSerial} | ${capacity} | Batch No ${batchNo}`;
};

const SrMultiSelectDropdown = ({
  label = "Select SR Numbers",
  options = [],
  selected = [],
  onChange,
  placeholder = "Search...",
  disabled = false,
  resetVersion
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const { position: dropdownPosition, maxHeight: dropdownMaxHeight, calculatePosition } = useDropdownPosition(wrapperRef, MENU_MAX_HEIGHT);

  // Reset state when resetVersion changes
  useEffect(() => {
    if (resetVersion !== undefined) {
      setQuery("");
      setIsOpen(false);
      onChange?.([]);
    }
  }, [resetVersion, onChange]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const filteredOptions = useMemo(() => {
    if (!query) return options;
    const lowerQuery = query.toLowerCase();
    return options.filter((option) => {
      const serial = getOptionSerial(option);
      const batchNo = option.batchNo || "";
      return serial.toLowerCase().includes(lowerQuery) || batchNo.toLowerCase().includes(lowerQuery);
    });
  }, [options, query]);

  // Selected SR numbers that don't exist in `options` (e.g. manually typed
  // in rather than picked from the list). Rendered as their own rows so
  // they're visible and can be unchecked from inside the dropdown too.
  const manualSelections = useMemo(() => {
    const known = new Set(options.map((option) => getOptionSerial(option)));
    const list = Array.isArray(selected) ? selected : [];
    const manual = list.filter((srNo) => srNo && !known.has(srNo));
    if (!query) return manual;
    const lowerQuery = query.toLowerCase();
    return manual.filter((srNo) => srNo.toLowerCase().includes(lowerQuery));
  }, [options, selected, query]);

  const toggleSelection = (srNo) => {
    if (!onChange) return;
    const current = Array.isArray(selected) ? selected : [];
    const next = current.includes(srNo)
      ? current.filter((item) => item !== srNo)
      : [...current, srNo];
    onChange(next);
  };

  const handleDone = () => {
    setIsOpen(false);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (event.key === "Escape") {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  const handleSearch = (searchTerm) => {
    setQuery(searchTerm);
    setIsOpen(true);
    calculatePosition();
  };

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        calculatePosition();
      }
    }
  };

  // Recalculate on resize/scroll while open, so the dropdown's width/height
  // stay correct relative to the input if the layout shifts (e.g. sidebar
  // collapse, window resize, or the page scrolling under a fixed header).
  useEffect(() => {
    if (!isOpen) return;
    const handleReposition = () => calculatePosition();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);
    return () => {
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [isOpen, calculatePosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const hasResults = filteredOptions.length > 0 || manualSelections.length > 0;

  return (
    <div className="sr-multiselect-wrapper" ref={wrapperRef}>
      <label className="sr-multiselect-label">{label}</label>
      <div className="erp-product-search-wrapper">
        <input
          type="text"
          className="erp-product-search-input"
          placeholder={selected?.length ? `${selected.length} selected` : placeholder}
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => !disabled && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          readOnly={false}
        />
        <div
          className="erp-product-search-chevron"
          onClick={(e) => {
            e.stopPropagation();
            handleToggle();
          }}
        >
          {isOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
        </div>
        {isOpen && (
          <div
            className="erp-product-dropdown sr-dropdown"
            style={{
              top: dropdownPosition === "top" ? "auto" : "calc(100% + 6px)",
              bottom: dropdownPosition === "top" ? "calc(100% + 6px)" : "auto",
              maxHeight: dropdownMaxHeight
            }}
          >
            <div className="sr-multiselect-options">
              {!hasResults ? (
                <div className="sr-multiselect-empty">
                  <span>No SR numbers found</span>
                </div>
              ) : (
                <>
                  {manualSelections.map((srNo) => (
                    <div
                      key={`manual-${srNo}`}
                      className="sr-multiselect-option sr-selected sr-manual-option"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelection(srNo);
                      }}
                    >
                      <div className="sr-multiselect-checkbox">
                        <FiCheck size={12} />
                      </div>
                      <span className="sr-multiselect-option-label">{srNo}</span>
                      <span className="sr-manual-tag">Manual</span>
                    </div>
                  ))}
                  {filteredOptions.map((option) => (
                    <div
                      key={getOptionSerial(option)}
                      className={`sr-multiselect-option ${selectedSet.has(getOptionSerial(option)) ? "sr-selected" : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelection(getOptionSerial(option));
                      }}
                    >
                      <div className="sr-multiselect-checkbox">
                        {selectedSet.has(getOptionSerial(option)) ? <FiCheck size={12} /> : <FiSquare size={12} />}
                      </div>
                      <span className="sr-multiselect-option-label">{getOptionLabel(option)}</span>
                    </div>
                  ))}
                </>
              )}
            </div>
            <div className="sr-multiselect-footer">
              <button
                type="button"
                className="sr-multiselect-done-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDone();
                }}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SrMultiSelectDropdown;